import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { prefersReducedMotion } from './motion';

/** Slide of the composer from the middle of the pane to the bottom (FLIP, product owner 2026-09-10). */
export const DOCK_MS = 420;
/** Fade-out of the welcome hero while the first turn appears. */
export const HERO_LEAVE_MS = 280;
export const DOCK_EASING = 'cubic-bezier(.2,.7,.2,1)';

export type HeroPhase = 'shown' | 'leaving' | 'gone';

/**
 * Empty thread → first message: the composer keeps its DOM node but the layout moves it from the centre to the
 * bottom. This hook measures where it was before the layout change and where it landed after (FLIP), then plays
 * the translation with the Web Animations API, while the hero is kept mounted for `HERO_LEAVE_MS` to fade out.
 * Returns the hero phase to render. Honours `prefers-reduced-motion` (no animation, hero unmounts at once).
 */
export function useEmptyTransition(empty: boolean, composerRef: RefObject<HTMLElement | null>): HeroPhase {
  const [hero, setHero] = useState<HeroPhase>(empty ? 'shown' : 'gone');
  const lastTop = useRef<number | null>(null);
  const wasEmpty = useRef(empty);
  const timer = useRef<number | null>(null);

  useEffect(() => () => { if (timer.current !== null) window.clearTimeout(timer.current); }, []);

  useLayoutEffect(() => {
    const el = composerRef.current;
    const flippedToChat = wasEmpty.current && !empty;
    const backToEmpty = !wasEmpty.current && empty;
    wasEmpty.current = empty;

    if (flippedToChat) {
      // No hero on screen to animate away when the composer was not rendered just before (a thread reopened
      // behind the blank start screen, or opened from the history): show the conversation at once.
      const reduced = prefersReducedMotion() || lastTop.current === null;
      if (el && lastTop.current !== null && !reduced && typeof el.animate === 'function') {
        const dy = lastTop.current - el.getBoundingClientRect().top;
        if (Math.abs(dy) > 1) {
          el.animate([{ transform: `translateY(${dy}px)` }, { transform: 'translateY(0)' }], { duration: DOCK_MS, easing: DOCK_EASING });
        }
      }
      if (timer.current !== null) window.clearTimeout(timer.current);
      if (reduced) {
        setHero('gone');
      } else {
        setHero('leaving');
        // The effect has no deps (it re-measures every render): the timer lives in a ref, cleared on unmount only.
        timer.current = window.setTimeout(() => setHero('gone'), HERO_LEAVE_MS);
      }
    } else if (backToEmpty) {
      if (timer.current !== null) window.clearTimeout(timer.current);
      setHero('shown');
    }
    // Remember where the composer sits after this render, for the next FLIP (none when it is not rendered).
    lastTop.current = el ? el.getBoundingClientRect().top : null;
  });

  return hero;
}
